import { useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { signInErrorKey } from "@heraion/shared";
import { toSupportedLng } from "@heraion/i18n";
import {
  Button,
  FieldLabel,
  PreauthLanguagePicker,
  resolveTheme,
  TextField,
  type UiTheme,
} from "@heraion/ui";
import { useAuth } from "./auth/AuthContext";
import type { AuthError } from "./auth/cognito";
import { useThemePalette } from "./theme";
import { ForgotPasswordGate } from "./ForgotPasswordGate";

// The web's sign-in screen. Born as a scaffold that only had to prove the
// react-native-web alias resolved end-to-end (View/Text/TextInput and the
// shared <Button> rendered on the DOM), and it kept the scaffold's copy and
// a11y long after it became the front door people actually sign in through.
// La porta di RECUPERO e' arrivata con RW-49 (il form condiviso, montato qui da
// ForgotPasswordGate). Della vecchia SignInPage resta fuori il solo
// confirm-code, che migra dopo.
//
// Theme-aware: every colour comes from the resolved palette (WebThemeProvider
// wraps this screen), so the sign-in shell follows the chosen theme + light/dark
// instead of the old hardcoded dark-only palette (which also carried a drifted
// brand orange `#ff7a1a` ≠ canonical brand500).
//
// RW-23: the submit control was a hand-rolled <Pressable> + <Text>, which
// react-native-web renders as a bare `div`, so the front door of the whole web
// announced itself as TEXT and not as a command. The keyboard DID reach it
// (r-n-w puts `tabindex=0` on the div, and Enter fired); what it did not do was
// activate on SPACE, which only a real <button> gets from the browser. Measured
// before and after, the table was in qa-harness.md §4 (emptied 02/10; see RW-23 in shipped.md). The shared <Button>
// already owns the role, the disabled/busy state and `webTapFloor` (the 44px
// floor r-n-w needs because its Pressable ignores hitSlop), so the fix is to
// STOP hand-rolling it, not to bolt an accessibilityRole onto the old box.
//
// RW-32/33/35: the rest of the scaffold's debt, measured the same way against
// the served build. The DOM carried two inputs whose only accessible name was a
// `placeholder` — which disappears the moment the field is focused — no
// `aria-live` on the error, no `aria-invalid`, and `autocomplete="on"`, which
// is r-n-w's default and tells a password manager nothing. The copy was
// hardcoded English reading "JackedLogs · web-rnw" / "react-native-web scaffold
// smoke": development strings, in production, on the first screen an athlete
// who reads in French ever sees. Every key used below already shipped
// translated in all five locales; this screen was simply the one surface that
// never called `useTranslation`. The label-above-the-field shape mirrors
// apps/mobile/src/screens/LoginScreen.tsx and CoachSettingsScreen.tsx, which
// already pair a visible <Text> with the same string as `accessibilityLabel`.
export function SignInForm() {
  const { t, i18n } = useTranslation();
  const { signIn } = useAuth();
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const styles = makeStyles(theme);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // RW-49 — la porta di recupero è uno STATO di questo schermo, non una rotta:
  // `App.tsx` è uno switch sull'AuthContext e non esiste un router. Stessa
  // forma che mobile ha già (`LoginScreen.tsx:26`, un `showForgot` locale),
  // così le due piattaforme hanno la stessa IA e non due modelli mentali.
  const [showForgot, setShowForgot] = useState(false);
  // Il messaggio che il reset lascia dietro di sé, letto sulla porta d'accesso
  // dove l'atleta atterra. Separato da `error`: è un esito riuscito.
  const [notice, setNotice] = useState<string | null>(null);

  const onSubmit = async () => {
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      const e = err as AuthError;
      // The key is only reachable through signInErrorKey: `toAuthError` always
      // supplies a `message`, so a bare `?? t(...)` would never fire and the
      // athlete would keep reading Cognito's English. Anything this does not
      // map still shows the provider's own copy — see RW-37.
      const key = signInErrorKey(e.code);
      setError(key !== null ? t(key) : (e.message ?? t("auth.signInFailed")));
      setBusy(false);
    }
  };

  if (showForgot) {
    return (
      <ForgotPasswordGate
        onDone={(message) => {
          setShowForgot(false);
          setError(null);
          setNotice(message);
          // La `notice` sotto porta una live-region, che copre Android e web.
          // Questo mount e' solo-web, quindi qui basta: la riga sta scritta
          // perche' il giorno che il form arrivasse su un altro host, l'esito
          // non diventi silenzioso senza che nessuno se ne accorga.
        }}
        onBack={() => setShowForgot(false)}
      />
    );
  }

  return (
    <View style={styles.wrap}>
      {/* #179: language first, system language by default (the detector). */}
      <PreauthLanguagePicker
        theme={theme}
        value={toSupportedLng(i18n.language)}
        onPick={(lng) => void i18n.changeLanguage(lng)}
      />
      {/* The web front door carries the PRODUCT name. BR-8 gave it a home:
          `brand.product` is the single place the name lives, same value in all
          five locales, so this reads it instead of re-declaring it. */}
      <Text style={styles.title}>{t("brand.product")}</Text>
      <Text style={styles.tagline}>{t("web.preauth.tagline")}</Text>
      <Text style={styles.statusLine}>{t("web.preauth.status")}</Text>
      <Text style={styles.subtitle}>{t("mobile.login.subtitle")}</Text>

      {notice ? (
        <Text style={styles.notice} accessibilityLiveRegion="polite">
          {notice}
        </Text>
      ) : null}

      <FieldLabel theme={theme} style={styles.label}>{t("auth.email")}</FieldLabel>
      <TextField
        theme={theme}
        accessibilityLabel={t("auth.email")}
        autoCapitalize="none"
        autoComplete="email"
        autoCorrect={false}
        editable={!busy}
        keyboardType="email-address"
        // The label carries the NAME; this carries the FORMAT, which is a
        // different job. Dropping it was an overcorrection: the defect was a
        // placeholder used AS the accessible name, not the placeholder itself.
        // Both siblings keep one (LoginScreen.tsx:93, CoachSettingsScreen.tsx:141).
        placeholder={t("auth.emailPlaceholder")}
        // Neither field can say WHICH half was wrong: Cognito answers one
        // opaque error for a bad pair. Marking both is the honest reading —
        // "one or both of these did not authenticate" — where marking one
        // would claim knowledge we do not have.
        invalid={error !== null}
        style={styles.field}
        value={email}
        onChangeText={setEmail}
      />

      <FieldLabel theme={theme} style={styles.label}>{t("auth.password")}</FieldLabel>
      <TextField
        theme={theme}
        accessibilityLabel={t("auth.password")}
        // r-n-w defaults a TextInput to `autocapitalize="sentences"`, which on
        // a phone browser capitalises the first letter of a typed password.
        autoCapitalize="none"
        autoComplete="current-password"
        autoCorrect={false}
        editable={!busy}
        invalid={error !== null}
        // Punctuation, not copy: no locale has its own bullet, so this one
        // stays out of the i18n bundle on purpose.
        placeholder="•••••••••"
        secureTextEntry
        style={styles.field}
        value={password}
        onChangeText={setPassword}
      />

      {error ? (
        // The one screen where failing is routine, so the failure has to reach
        // someone who is not looking at it. Same idiom as EditorTopBar.tsx:90,
        // the only other error line beside a submit in the repo.
        <Text style={styles.error} accessibilityLiveRegion="polite">
          {error}
        </Text>
      ) : null}

      {/* RW-38: aria-busy on the button is the machine half; a polite live
          region is the half a screen reader says out loud. */}
      {busy ? (
        <Text style={styles.notice} accessibilityLiveRegion="polite">
          {t("auth.submitting")}
        </Text>
      ) : null}

      <Button
        theme={theme}
        label={t("auth.signIn")}
        loading={busy}
        onPress={onSubmit}
      />

      {/* RW-49 — la via al recupero. Riusa l'idioma del link che sta già in
          questo file (`contactHit`): testo sottolineato, nessuna scatola
          gonfiata, e il pavimento dei 44 al callsite perché react-native-web
          ignora `hitSlop`. */}
      <Pressable
        accessibilityRole="button"
        style={styles.contactHit}
        onPress={() => {
          setError(null);
          setNotice(null);
          setShowForgot(true);
        }}
      >
        <Text style={styles.contact}>{t("auth.forgotLink")}</Text>
      </Pressable>

      {/* Registration is closed, so this is the one door most first-time visitors
          reach. It has to tell them where to go instead of a dead sign-in form. */}
      <View style={styles.contactBlock}>
        <Text style={styles.contactLabel}>{t("web.preauth.contactLabel")}</Text>
        <Pressable
          accessibilityRole="link"
          style={styles.contactHit}
          onPress={() => {
            window.location.href = "mailto:metria+dev@heraion.app";
          }}
        >
          <Text style={styles.contact}>metria+dev@heraion.app</Text>
        </Pressable>
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    // RW-47 — `paddingHorizontal` e non solo la larghezza: a 320dp una colonna
    // larga esattamente 320 va a filo dei due bordi e il testo tocca il vetro.
    // Stesso difetto e stessa cura del fratello `WelcomeForm.tsx:241-249`.
    wrap: {
      width: 320,
      maxWidth: "100%",
      paddingHorizontal: s.s4,
      gap: s.s4,
    },
    title: {
      color: p.ink,
      fontSize: ty.sizes.xl,
      fontWeight: "700",
    },
    subtitle: {
      color: p.ink2,
      fontSize: ty.sizes.base,
      marginBottom: s.s3,
    },
    // ⛔ 44 al CALLSITE e non dentro `TextField`: `PrescribeHeaderFields.tsx:113`
    // ha già deciso che 36 è giusto per le quaranta forme fitte che quel
    // primitivo serve, e sbagliato per il primo controllo che un pollice
    // incontra. Questa è la porta del web, e qui i campi SONO lo schermo —
    // stessa riga che i tre schermi mobile della porta portano ora.
    field: { minHeight: 44 },
    // La tipografia la porta `FieldLabel`; qui resta solo la spaziatura.
    label: { marginBottom: s.s2 },
    error: {
      color: p.danger,
      fontSize: ty.sizes.base,
    },
    tagline: {
      color: p.ink2,
      fontSize: ty.sizes.base,
      lineHeight: ty.sizes.base * ty.leading.base,
      marginTop: s.s3,
    },
    statusLine: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      marginTop: s.s3,
    },
    contactBlock: {
      marginTop: s.s6,
      gap: s.s2,
    },
    contactLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    // The mailto is the primary door while registration is closed, so its tap
    // target must clear the 44px floor (RW-23). web-rnw is web-only, so the
    // literal is exactly what `webTapFloor` resolves to here.
    contactHit: {
      minHeight: 44,
      justifyContent: "center",
      alignSelf: "flex-start",
    },
    contact: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      textDecorationLine: "underline",
    },
    // L'esito del reset, letto qui perché è qui che l'atleta atterra.
    notice: { color: p.ink2, fontSize: ty.sizes.sm },
  });
}
